import { decodeJwtClaims, readPlacement } from './oidc-adapter';
import type { AuthAdapter, AuthSession } from './types';
import { TOKEN_REFRESH_BUFFER_MS } from './types';
import type { RawGrant } from '../permissions/types';

/**
 * The session bridge from the legacy admin (SDD-012, U-6').
 *
 * The console lives on its own origin, so the legacy's `localStorage` cannot be read. What CAN
 * cross the gap is a `postMessage` between the two windows: the legacy admin opens the console in
 * a named window, this adapter asks its opener for the session, and the patched legacy replies
 * with what it keeps under `APP_TOKEN` — `{ username, accessToken, permissions }`. Nothing is
 * persisted here: the session lives in memory for the life of the window, exactly like the OIDC
 * adapter's, and no token ever appears in a URL.
 *
 * Trust is one origin wide. The request is posted with the legacy origin as `targetOrigin`, so a
 * window on any other origin never receives it; a reply is accepted only when it comes FROM that
 * origin AND from the opener itself. A malformed reply, a token that is not a JWT, or one already
 * expired is discarded — the console then has no session, and a console with no session is sent
 * back to the suite to enter again. It never fails open.
 *
 * Refresh reuses the legacy's own endpoint. `POST {legacyApiBaseUrl}/authservice/v1/refresh-token`
 * with the username in the body and `credentials: 'include'`: the refresh token is an HttpOnly
 * cookie the legacy's API set, and `*.emil.de` is one site, so the cookie travels. The API must
 * list the console's origin in CORS (the CloudFront runbook says so). The response is the legacy
 * envelope `{ accessToken, permissions, newPasswordRequired }`, the same shape the hand-over used.
 *
 * Why not the SDKs (T-7): the legacy authservice is not an EIS platform service and has no
 * generated client here; like the OIDC adapter's token endpoint, this is the auth layer talking to
 * its issuer, which is what the adapter interface exists to isolate.
 *
 * The SDK's session middleware (SDD-019 S-6, S-11) uses this SAME refresh: `sessionRefresher`
 * calls it when a request comes back 401/403, and the refresh URL is a session endpoint the
 * middleware never retries. The request itself — endpoint, body, cookie — is unchanged (S-10).
 * Production runs exactly this: the legacy authservice, its cookie and Cognito are not modified.
 */
export interface LegacySessionConfig {
  /** The legacy admin's origin, e.g. `https://app.emil.de`. The one origin trusted for the hand-over. */
  readonly legacyOrigin: string;
  /** The legacy API origin, e.g. `https://apiv2-test.emil.de`. Where refresh goes. */
  readonly legacyApiBaseUrl: string;
  /** The suite's address, where a window without a session is sent. */
  readonly suiteUrl: string;
  /** How long to wait for the opener's reply before concluding there is no session. */
  readonly handoverTimeoutMs?: number;
}

/** What the legacy admin keeps under `APP_TOKEN` (`Services/auth.ts:281-288`), and hands over. */
export interface LegacySessionPayload {
  readonly username: string;
  readonly accessToken: string;
  readonly permissions: readonly RawGrant[];
}

export const SESSION_REQUEST_TYPE = 'eis:session-request';
export const SESSION_REPLY_TYPE = 'eis:session';
export const LEGACY_REFRESH_PATH = '/authservice/v1/refresh-token';
export const SUITE_DASHBOARD_PATH = '/u/0/dashboard';

const DEFAULT_HANDOVER_TIMEOUT_MS = 3000;

interface SessionRequestMessage {
  readonly type: typeof SESSION_REQUEST_TYPE;
}

/** The legacy envelope, as `Services/api/auth/base.ts::TokenResponse` declares it. */
interface LegacyTokenResponse {
  readonly accessToken: string;
  readonly permissions?: readonly RawGrant[];
  readonly newPasswordRequired?: boolean;
}

const trimSlash = (url: string): string => url.replace(/\/+$/, '');

function isStringArray(value: unknown): value is readonly string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}

/** Shape-checks the hand-over payload. Anything unexpected is not a session. */
export function readLegacySessionPayload(data: unknown): LegacySessionPayload | null {
  if (typeof data !== 'object' || data === null) return null;
  const record = data as Record<string, unknown>;
  if (record.type !== SESSION_REPLY_TYPE) return null;
  const { username, accessToken, permissions } = record;
  if (typeof username !== 'string' || username === '') return null;
  if (typeof accessToken !== 'string' || accessToken.split('.').length !== 3) return null;
  if (!isStringArray(permissions)) return null;
  return { username, accessToken, permissions };
}

/**
 * A session from a legacy access token and the grants beside it (T-14: the array beside the token
 * is an accepted source). Expiry comes from the JWT's `exp`; a token without one, or with one in
 * the past, is not a session. The legacy issuer stamps `custom:tenant_id/slug/name` and `custom:role`
 * but no placement claim, so placement is honestly `unknown`.
 */
export function sessionFromLegacyToken(
  payload: LegacySessionPayload,
  now: number = Date.now(),
): AuthSession | null {
  let claims;
  try {
    claims = decodeJwtClaims(payload.accessToken);
  } catch {
    return null;
  }
  if (typeof claims.exp !== 'number') return null;
  const expiresAt = claims.exp * 1000;
  if (expiresAt <= now) return null;

  const tenantId = claims['custom:tenant_id'] ?? 'unknown';
  const slug = claims['custom:tenant_slug'] ?? tenantId;
  const fullName = [claims.given_name, claims.family_name].filter(Boolean).join(' ');
  return {
    user: {
      id: claims.sub ?? 'unknown',
      email: claims.email ?? payload.username,
      displayName:
        claims.name ?? (fullName === '' ? undefined : fullName) ?? claims.email ?? payload.username,
      role: claims['custom:role'] ?? 'unknown',
    },
    tenant: {
      id: tenantId,
      slug,
      name: claims['custom:tenant_name'] ?? slug,
      nameFromClaim: claims['custom:tenant_name'] !== undefined,
      placement: readPlacement(claims['custom:tenant_type']),
      location: claims['custom:tenant_location'] ?? null,
    },
    grants: payload.permissions,
    accessToken: payload.accessToken,
    expiresAt,
  };
}

/** The slice of `window` the hand-over needs, so a test can fake the opener and the messages. */
export interface HandoverWindow {
  readonly opener: { postMessage: (message: unknown, targetOrigin: string) => void } | null;
  addEventListener: (type: 'message', listener: (event: MessageEvent) => void) => void;
  removeEventListener: (type: 'message', listener: (event: MessageEvent) => void) => void;
  location: { assign: (url: string) => void };
}

/**
 * Asks the opener for the session and waits for the one reply that is allowed: from the
 * configured legacy origin, from the opener itself, in the agreed shape. Resolves null on
 * anything else, including silence.
 */
export function requestSessionFromOpener(
  win: HandoverWindow,
  legacyOrigin: string,
  timeoutMs: number,
): Promise<LegacySessionPayload | null> {
  const opener = win.opener;
  if (opener === null) return Promise.resolve(null);

  return new Promise((resolve) => {
    const finish = (payload: LegacySessionPayload | null): void => {
      clearTimeout(timer);
      win.removeEventListener('message', onMessage);
      resolve(payload);
    };
    const onMessage = (event: MessageEvent): void => {
      // Origin first, source second, shape last: a reply from anywhere else is not looked at.
      if (event.origin !== legacyOrigin) return;
      if (event.source !== opener) return;
      const payload = readLegacySessionPayload(event.data);
      if (payload === null) return;
      finish(payload);
    };
    const timer = setTimeout(() => {
      finish(null);
    }, timeoutMs);
    win.addEventListener('message', onMessage);
    const request: SessionRequestMessage = { type: SESSION_REQUEST_TYPE };
    opener.postMessage(request, legacyOrigin);
  });
}

export function createLegacySessionAdapter(
  config: LegacySessionConfig,
  win: () => HandoverWindow = () => globalThis.window,
): AuthAdapter {
  if (config.legacyOrigin === '' || config.legacyApiBaseUrl === '') {
    throw new Error(
      'The legacy-session adapter needs auth.legacyOrigin and auth.legacyApiBaseUrl in ' +
        'config.json. Provider "legacy-session" without them would trust no window and refresh nowhere.',
    );
  }
  const legacyOrigin = trimSlash(config.legacyOrigin);
  const refreshUrl = `${trimSlash(config.legacyApiBaseUrl)}${LEGACY_REFRESH_PATH}`;
  const suiteDashboard = `${trimSlash(config.suiteUrl)}${SUITE_DASHBOARD_PATH}`;
  const timeoutMs = config.handoverTimeoutMs ?? DEFAULT_HANDOVER_TIMEOUT_MS;

  let session: AuthSession | null = null;
  let username: string | null = null;
  /** One in-flight refresh at a time: three parallel queries must not refresh three times. */
  let refreshing: Promise<AuthSession> | null = null;

  const refresh = async (): Promise<AuthSession> => {
    if (username === null)
      throw new Error('No legacy session to refresh: enter through the suite.');
    refreshing ??= (async () => {
      const response = await fetch(refreshUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username }),
        // The refresh token is the legacy API's HttpOnly cookie; this is what sends it along.
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`Legacy session refresh failed (${String(response.status)}).`);
      }
      const payload = (await response.json()) as LegacyTokenResponse;
      const next = sessionFromLegacyToken({
        username: username ?? '',
        accessToken: payload.accessToken,
        permissions: payload.permissions ?? [],
      });
      if (next === null) throw new Error('Legacy session refresh returned no usable token.');
      session = next;
      return next;
    })().finally(() => {
      refreshing = null;
    });
    return refreshing;
  };

  /** A window with no session has one place to go: the suite, to enter again. Path only. */
  const backToSuite = (): void => {
    session = null;
    username = null;
    win().location.assign(suiteDashboard);
  };

  /**
   * The legacy refresh is gone (locally there is none — deploy/Caddyfile) or refused. Before
   * giving the window up, ask the OPENER once more: the suite may hold a newer session by now
   * (somebody signed in again there), and handing this window back to a suite that would reopen
   * it with the same dying token is a bounce, not a sign-in. Null: the window went back.
   */
  const recoverFromOpenerOrLeave = async (): Promise<string | null> => {
    const again = await requestSessionFromOpener(win(), legacyOrigin, timeoutMs);
    const next = again === null ? null : sessionFromLegacyToken(again);
    if (next !== null && next.expiresAt - Date.now() > TOKEN_REFRESH_BUFFER_MS) {
      session = next;
      username = again?.username ?? username;
      return next.accessToken;
    }
    // An expired session that cannot be refreshed is no session. Back through the suite.
    backToSuite();
    return null;
  };

  /** One recovery at a time, however many clients report the session lost. */
  let recovering: Promise<string | null> | null = null;
  const recoverOnce = (): Promise<string | null> => {
    recovering ??= recoverFromOpenerOrLeave().finally(() => {
      recovering = null;
    });
    return recovering;
  };

  return {
    kind: 'legacy-session',

    async restore() {
      if (session !== null) return session;
      const payload = await requestSessionFromOpener(win(), legacyOrigin, timeoutMs);
      if (payload === null) return null;
      const next = sessionFromLegacyToken(payload);
      if (next === null) return null;
      session = next;
      username = payload.username;
      return next;
    },

    signIn() {
      // There is no interactive flow of its own: the legacy admin is the login. This document is
      // being replaced by the suite, so there is no session to return (see AuthAdapter.signIn).
      backToSuite();
      return Promise.resolve(null);
    },

    refresh,

    async getAccessToken() {
      if (session === null) return null;
      if (session.expiresAt - Date.now() > TOKEN_REFRESH_BUFFER_MS) return session.accessToken;
      try {
        return (await refresh()).accessToken;
      } catch {
        return recoverOnce();
      }
    },

    async sessionRefresher() {
      try {
        return (await refresh()).accessToken;
      } catch {
        return null;
      }
    },

    onSessionLost() {
      // A refused legacy refresh: the opener once more, then the suite — as a failed refresh
      // ahead of expiry always did.
      void recoverOnce();
    },

    isSessionEndpoint: (url: string) => {
      try {
        return new URL(url).href === new URL(refreshUrl).href;
      } catch {
        return false;
      }
    },

    signOut() {
      // The session is the legacy's; the console only forgets its copy and returns to the suite,
      // which owns logging out (`/u/<idx>/logout`).
      backToSuite();
      return Promise.resolve();
    },
  };
}
